import * as React from 'react';

interface IAsAsyncState<TProps> {
  component?: React.ComponentType<TProps> | undefined;
  error?: Error;
}

/**
 * If possible, use a WeakMap to maintain a cache of loaded components.
 * This can be used to synchronously render components that have already been loaded,
 * rather than having to wait for at least one async tick.
 */
const syncModuleCache =
  typeof WeakMap !== 'undefined'
    ? new WeakMap<() => Promise<any>, React.ComponentType<any>>() // tslint:disable-line:no-any
    : undefined;

/**
 * Represents the surface of a module which has a default export.
 * @public
 */
export interface IModuleWithDefault<T> {
  default: T;
}

/**
 * Produces a component which internally loads the target component before first mount.
 * The component passes all props through to the loaded component.
 *
 * This overload accepts a module with a default export for the component.
 * @public
 */
export function asAsync<TProps>(
  load: () => Promise<{ default: React.ComponentType<TProps> }>
): React.ComponentType<TProps>;
/**
 * Produces a component which internally loads the target component before first mount.
 * The component passes all props through to the loaded component.
 *
 * This overload accepts a component directly.
 * @public
 */
export function asAsync<TProps>(
  load: () => Promise<React.ComponentType<TProps>>
): React.ComponentType<TProps>;
/**
 * Implementation of asAsync.
 * @public
 */
export function asAsync<TProps>(
  load: () => Promise<React.ComponentType<TProps> | IModuleWithDefault<React.ComponentType<TProps>>>
): React.ComponentType<TProps> {
  class Async extends React.PureComponent<TProps, IAsAsyncState<TProps>> {
    private _isDisposed?: boolean;

    constructor(props: TProps) {
      super(props);

      this.state = {
        ...(syncModuleCache && syncModuleCache.has(load)
          ? {
            component: syncModuleCache.get(load)
          }
          : {})
      };
    }

    public async componentDidMount(): Promise<void> {
      if (this.state.component) {
        return;
      }

      // Resolve the component during this lifecycle method so
      // if the promise resolves synchronously, setState does not trigger another update.
      try {
        const component = getModuleDefaultOrModule(await load());

        if (syncModuleCache) {
          syncModuleCache.set(load, component);
        }

        if (this._isDisposed) {
          return;
        }

        this.setState({
          component: component
        });
      } catch (e) {
        if (this._isDisposed) {
          return;
        }

        this.setState({
          error: e instanceof Error ? e : new Error(`${e}`)
        });
      }
    }

    public render(): JSX.Element | null {
      const { component: Component, error } = this.state;

      if (error) {
        // Async load errors are significant, so expect the host to use an error boundary to catch and handle them.
        throw error;
      }

      return Component ? (
        <Component { ...this.props } />
      ) : null;
    }

    public componentWillUnmount(): void {
      this._isDisposed = true;
    }
  }

  return Async;
}

/**
 * For a given type, gets the default export from a module, or gets the whole module if there is no
 * default export.
 * @public
 */
export function getModuleDefaultOrModule<T>(valueOrModuleWithDefault: T | IModuleWithDefault<T>): T {
  return (valueOrModuleWithDefault as IModuleWithDefault<T>).default || valueOrModuleWithDefault;
}
